'use client'

import { useEffect } from 'react'
import { X, ShoppingBag, ArrowRight } from 'lucide-react'
import { Button } from '@/components/Button'
import { CartItem } from '@/components/CartItem'
import { useCartStore } from '@/stores/cartStore'
import Link from 'next/link'
import formatCurrency from '@/lib/common/utils/formatCurrency'

export function CartSidebar() {
  const { 
    items, 
    isOpen, 
    closeCart, 
    totalItems, 
    totalPrice, 
    clearCart 
  } = useCartStore()

  // Close cart on escape key and handle body scroll
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeCart()
      }
    }

    if (isOpen) {
      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
      
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
      document.body.style.paddingRight = '0px'
    }
  }, [isOpen, closeCart])

  if (!isOpen) return null

  const isEmpty = items.length === 0
  const total = totalPrice()
  const itemCount = totalItems()

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={closeCart}
      />

      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-900">
              Winkelwagen
              {itemCount > 0 && (
                <span className="ml-2 text-sm font-normal text-slate-600">
                  ({itemCount} {itemCount === 1 ? 'item' : 'items'})
                </span>
              )}
            </h2>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={closeCart}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {isEmpty ? (
            /* Empty State */
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center">
                <ShoppingBag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  Je winkelwagen is leeg
                </h3>
                <p className="text-slate-600 mb-6">
                  Ontdek onze producten en voeg ze toe aan je winkelwagen
                </p>
                <Button asChild onClick={closeCart}>
                  <Link href="/producten">
                    Bekijk producten
                  </Link>
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Items List */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {items.map((item) => (
                  <CartItem 
                    key={item.id} 
                    item={item}
                    showRemoveButton={true}
                    showQuantityControls={true}
                  />
                ))}
              </div>

              {/* Footer */}
              <div className="border-t border-slate-200 p-6 space-y-4">
                {/* Total */}
                <div className="flex justify-between items-center text-lg font-semibold">
                  <span>Totaal:</span>
                  <span>{formatCurrency(total)}</span>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button 
                    size="lg" 
                    className="w-full"
                    asChild
                  >
                    <Link href="/afrekenen" onClick={closeCart}>
                      Afrekenen
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      asChild
                    >
                      <Link href="/producten" onClick={closeCart}>
                        Verder winkelen
                      </Link>
                    </Button>
                    
                    <Button 
                      variant="ghost" 
                      onClick={clearCart}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Leegmaken
                    </Button>
                  </div>
                </div>

                {/* Info Text */}
                <p className="text-xs text-slate-500 text-center">
                  Verzendkosten worden berekend bij het afrekenen
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}